// src/components/layout/MobileNav.jsx
// STATUS: active | UPDATED: 2026-10-02
// LOG:
//   2026-10-02  Notes and Your manager system in the More sheet for Jazz.
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
//   2026-08-01  Tabs now Today / Training / Coach + More sheet; safe-area padding
// Fixed bottom nav for mobile. Mirrors the sidebar language so mobile and
// desktop use the same words: Today, Training, Coach. The fourth slot is
// More, which opens a bottom sheet (Calendar, Team, Settings, Admin, Log out).
//
// Breakpoint matches Sidebar exactly: sidebar shows at lg and up, this shows
// below lg. One breakpoint, never both at once.
//
// Admin is CONDITIONALLY RENDERED, not disabled. A user without the admin role
// never sees it exist. Note that hiding a nav item is cosmetic only: the real
// protection has to live in ProtectedRoute and in Supabase RLS.
//
// signOut is imported from lib/auth, matching Header, so both surfaces use the
// same sign out path.
//
// Safe area: pb uses env(safe-area-inset-bottom) so the bar clears the iPhone
// home indicator instead of sitting under it.

import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Box, HStack, VStack, Text, Icon,
  Drawer, DrawerOverlay, DrawerContent, DrawerBody,
  useDisclosure,
} from '@chakra-ui/react'
import {
  LayoutDashboard, Map, Sparkles, Calendar, MoreHorizontal,
  Users, Settings, ShieldCheck, LogOut, BarChart3, FileText, ClipboardCheck,
} from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { mobileNavFor, atLeast } from '../../lib/nav'
import { signOut } from '../../lib/auth'

const ICONS = { LayoutDashboard, Map, Sparkles, Users }

function ActiveTick({ show }) {
  return (
    <Box
      position="absolute"
      top={0}
      left="50%"
      transform="translateX(-50%)"
      w={show ? '18px' : '0'}
      h="2px"
      bg="ink"
      borderRadius="full"
      transition="width 200ms"
      aria-hidden="true"
    />
  )
}

function TabItem({ to, label, icon: IconComponent }) {
  const location = useLocation()
  const isActive = location.pathname.startsWith(to)
  return (
    <NavLink to={to} style={{ textDecoration: 'none', flex: 1 }}>
      <VStack
        spacing={1}
        py={2}
        color={isActive ? 'ink' : 'inkDim'}
        transition="color 150ms"
        position="relative"
      >
        <ActiveTick show={isActive} />
        <Icon as={IconComponent} boxSize={5} strokeWidth={isActive ? 2.4 : 2} />
        <Text fontSize="11px" fontWeight={isActive ? 600 : 500} letterSpacing="-0.01em">
          {label}
        </Text>
      </VStack>
    </NavLink>
  )
}

function SheetRow({ icon: IconComponent, label, onClick, danger = false }) {
  return (
    <HStack
      as="button"
      onClick={onClick}
      w="full"
      px={5}
      py={4}
      spacing={4}
      textAlign="left"
      color={danger ? 'danger' : 'ink'}
      _hover={{ bg: 'surface' }}
      _active={{ bg: 'surface' }}
      transition="background 120ms"
      borderRadius="md"
    >
      <Icon as={IconComponent} boxSize={5} color={danger ? 'danger' : 'inkMuted'} />
      <Text fontSize="md" fontWeight={500}>
        {label}
      </Text>
    </HStack>
  )
}

export default function MobileNav() {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const navigate = useNavigate()
  const location = useLocation()
  const { role, isAdmin, profile } = useAuth()
  // A manager gains a fourth tab for their floor. Everyone else keeps three,
  // because a 375px bar loses legibility fast and a salesperson has no floor.
  // Same split as the sidebar: the author shares the role and not the job.
  const author = profile?.is_method_author === true
  const tabs = mobileNavFor(role, { author })

  // /team/ counts as "more" only for people who do not have it as a tab.
  const moreIsActive =
    location.pathname.startsWith('/calendar/') ||
    location.pathname.startsWith('/settings/') ||
    location.pathname.startsWith('/admin/') ||
    (!atLeast(role, 'manager') && location.pathname.startsWith('/team/'))

  const go = (path) => {
    onClose()
    navigate(path)
  }

  const handleSignOut = async () => {
    onClose()
    await signOut()
    navigate('/login/', { replace: true })
  }

  return (
    <>
      <Box
        as="nav"
        aria-label="Main navigation"
        display={{ base: 'block', lg: 'none' }}
        position="fixed"
        /* Floating bar rather than a slab welded to the bottom edge. It keeps
           a 12px gutter on both sides and clears the home indicator by the
           same amount, so it reads as an object sitting on the page instead of
           the page ending in a grey band. Wide, semi-rounded, see-through. */
        bottom="calc(env(safe-area-inset-bottom) + 12px)"
        left={3}
        right={3}
        zIndex={40}
        bg="navGlass"
        backdropFilter="saturate(180%) blur(20px)"
        border="1px solid"
        borderColor="line"
        borderRadius="cardLg"
        /* Lifts it off whatever is scrolling underneath. Without this the
           translucency reads as a rendering glitch rather than a material. */
        boxShadow="0 6px 24px rgba(0,0,0,0.14)"
        overflow="hidden"
      >
        {/* The active tick sits at the top of each tab, so the row needs a
            little padding or the tick lands flush against a rounded edge. */}
        <HStack spacing={0} align="stretch" py={1}>
          {tabs.map((item) => (
            <TabItem key={item.key} to={item.to} label={item.label} icon={ICONS[item.icon]} />
          ))}

          {/* More: opens the sheet, does not navigate */}
          <VStack
            as="button"
            onClick={onOpen}
            aria-label="More"
            flex={1}
            spacing={1}
            py={2}
            color={moreIsActive ? 'ink' : 'inkDim'}
            transition="color 150ms"
            position="relative"
          >
            <ActiveTick show={moreIsActive} />
            <Icon as={MoreHorizontal} boxSize={5} strokeWidth={moreIsActive ? 2.4 : 2} />
            <Text fontSize="11px" fontWeight={moreIsActive ? 600 : 500} letterSpacing="-0.01em">
              More
            </Text>
          </VStack>
        </HStack>
      </Box>

      <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
        <DrawerOverlay bg="blackAlpha.500" />
        <DrawerContent
          bg="card"
          borderTopRadius="2xl"
          pb="calc(env(safe-area-inset-bottom) + 1rem)"
        >
          <DrawerBody px={3} pt={3} pb={0}>
            {/* grab handle */}
            <Box
              w="36px"
              h="4px"
              bg="line"
              borderRadius="full"
              mx="auto"
              mb={3}
              aria-hidden="true"
            />
            <VStack align="stretch" spacing={0}>
              <SheetRow icon={Calendar} label="Calendar" onClick={() => go('/calendar/')} />
              {/* The floor is a tab for a manager, so repeating it here is
                  clutter, and a salesperson has no floor to look at. Either way
                  it does not belong in this sheet. */}
              {/* The numbers are an owner's job and a desk job, so they are
                  not worth a tab on a 375px bar. Reachable here rather than
                  nowhere: /numbers/ is owner gated and the mobile tab list is a
                  whitelist, so without this row a phone could not reach it. */}
              {atLeast(role, 'owner') && !author && (
                <SheetRow icon={BarChart3} label="The numbers" onClick={() => go('/numbers/')} />
              )}

              {/* The two pages Jazz asked for on 2026-09-27, on the phone he
                  dictates from. Same pair as AUTHOR_EXTRAS in nav.js. */}
              {author && (
                <>
                  <SheetRow icon={FileText} label="Notes" onClick={() => go('/notes/')} />
                  <SheetRow icon={ClipboardCheck} label="Your manager system" onClick={() => go('/admin/manager-review/')} />
                </>
              )}

              <SheetRow icon={Settings} label="Settings" onClick={() => go('/settings/')} />

              {/* Admin only exists for admins. Not greyed out, absent. */}
              {isAdmin && !author && (
                <SheetRow icon={ShieldCheck} label="Admin" onClick={() => go('/admin/')} />
              )}

              <Box h="1px" bg="line" my={2} aria-hidden="true" />

              <SheetRow icon={LogOut} label="Log out" onClick={handleSignOut} danger />
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}